import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createSignal, For, Show } from "solid-js";
import { useNavigate } from "@/lib/u-router";
import { Menu, Monitor, Moon, Palette, Sparkles, Sun } from "lucide-solid";
import { cn } from "@mealplan/shared";

import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { type ColorMode, useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";

interface AppHeaderProps {
  onMenuClick: () => void;
  isNavigating: () => boolean;
}

/**
 * Control de apariencia canónico (Sistema / Claro / Oscuro), disponible para
 * TODOS — con y sin login. Reemplaza al antiguo botón Sol/Luna que solo
 * navegaba a la galería. Los temas personalizados (más opciones) viven en
 * /themes y requieren sesión.
 */
function AppearanceToggle() {
  const theme = useTheme();
  const { t } = useI18n();
  const [open, setOpen] = createSignal(false);

  const choose = (mode: ColorMode) => {
    theme.selectColorMode(mode);
    setOpen(false);
  };

  // Guardamos el descriptor `msg` (no la traducción) y llamamos `t()` en el render
  // para que el label sea reactivo al cambio de idioma.
  const options: { mode: ColorMode; label: MessageDescriptor; icon: typeof Sun }[] = [
    { mode: "system", label: msg`System`, icon: Monitor },
    { mode: "light", label: msg`Light`, icon: Sun },
    { mode: "dark", label: msg`Dark`, icon: Moon },
  ];

  return (
    <Popover open={open()} onOpenChange={setOpen}>
      <PopoverTrigger
        as="button"
        type="button"
        class={cn(
          buttonVariants({ variant: "ghost", size: "icon" }),
          "h-8 w-8 text-muted-foreground hover:text-foreground",
        )}
        title={t(msg`Appearance`)}
        aria-label={t(msg`Appearance`)}
      >
        <Show when={theme.isDark()} fallback={<Sun class="h-4 w-4" />}>
          <Moon class="h-4 w-4" />
        </Show>
      </PopoverTrigger>
      <PopoverContent class="w-44 p-1.5">
        <p class="px-2 py-1 text-xs font-semibold text-muted-foreground">{t(msg`Appearance`)}</p>
        <For each={options}>
          {(opt) => (
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50 data-[active=true]:bg-accent/40 data-[active=true]:font-semibold"
              data-active={theme.colorMode() === opt.mode}
              onClick={() => choose(opt.mode)}
            >
              <opt.icon class="h-4 w-4" />
              <span>{t(opt.label)}</span>
            </button>
          )}
        </For>
      </PopoverContent>
    </Popover>
  );
}

export function AppHeader(props: AppHeaderProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useI18n();

  return (
    <header class="relative flex h-16 items-center justify-between border-b border-border/20 bg-card/5 px-4 backdrop-blur-sm md:px-6">
      {/* Subtle Loading Bar */}
      <div
        class="absolute bottom-[-1px] left-0 h-[2px] bg-accent transition-[width,opacity] duration-500 ease-out z-50"
        style={{
          width: props.isNavigating() ? "70%" : "0%",
          opacity: props.isNavigating() ? "1" : "0",
          "box-shadow": props.isNavigating() ? "0 0 8px var(--accent)" : "none",
        }}
      />
      <div class="flex items-center gap-4 flex-1">
        <Button
          variant="ghost"
          size="icon"
          class="h-11 w-11 lg:hidden text-muted-foreground hover:text-foreground"
          title={t(msg`Open menu`)}
          aria-label={t(msg`Open menu`)}
          onClick={props.onMenuClick}
        >
          <Menu class="h-5 w-5" />
        </Button>
      </div>
      <div class="flex items-center gap-1">
        {/* Blur toggle — mirrors native ThemeToggle */}
        <Button
          variant="ghost"
          size="icon"
          class="h-8 w-8 text-muted-foreground hover:text-foreground"
          title={theme.isBlur() ? t(msg`Disable blur effect`) : t(msg`Enable blur effect`)}
          onClick={() => {
            void theme.toggleBlur();
          }}
        >
          <Show when={theme.isBlur()} fallback={<Sparkles class="h-4 w-4" />}>
            <Sparkles class="h-4 w-4 text-primary" />
          </Show>
        </Button>
        {/* Appearance: System / Light / Dark — works without login */}
        <AppearanceToggle />
        {/* Palette shortcut to theme gallery (custom themes, login for more) */}
        <Button
          variant="ghost"
          size="icon"
          class="h-8 w-8 text-muted-foreground hover:text-foreground"
          title={t(msg`Themes`)}
          onClick={() => void navigate({ to: "/themes" })}
        >
          <Palette class="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}
